/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    ContentPreviewGetV2Request,
    OfferPreviewGetV2Request,
    PreviewTagApi,
    RecordingPreviewGetV2Request,
    ScreenType,
} from 'client-code/previews';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IPreviewsServiceRepository } from '../interfaces';
import { IRecordingPreviewParams } from '../interfaces/IPreviewService';
import { Log } from '../logging';
import {
    TContentPreview,
    TOfferPreview,
    TServicesParams,
    TUnifiedCollectionPreview,
    TUnifiedCollectionPreviewParams,
} from '../types';
import { UniqueIdentifier } from '../utils';

export class PreviewsServiceRepositoryImplV2 implements IPreviewsServiceRepository {
    private static TAG: string = 'PreviewsServiceRepositoryImplV2';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getConfigurationParameters(commonHeaders): ConfigurationParameters {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
    }

    private getCommonRequestParams(commonHeaders: CommonHeaders['headers']) {
        return {
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            timeZoneName: this.mParams.timeZoneName,
            applicationName: commonHeaders.ApplicationName,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private async getContentPreviewFromServiceV2(contentId: string): Promise<TContentPreview> {
        try {
            const commonHeaders = new CommonHeaders(ScreenType.SeriesScreenItem).headers;
            const contentPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(contentPreviewConfigParameters);
            const previewsApi = new PreviewTagApi(configuration);
            const contentPreviewV2RequestParameters: ContentPreviewGetV2Request = {
                contentId,
                ...this.getCommonRequestParams(commonHeaders),
            };
            const previewsContentResponse = await previewsApi.contentPreviewGetV2(
                contentPreviewV2RequestParameters,
            );

            return previewsContentResponse.data;
        } catch (error: any) {
            Log.e(PreviewsServiceRepositoryImplV2.TAG, 'getContentPreviewFromServiceV2()', error);
            throw error;
        }
    }

    private async getOfferPreviewFromServiceV2(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            const commonHeaders = new CommonHeaders(screenType).headers;

            const offerPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);

            const configuration = new Configuration(offerPreviewConfigParameters);
            const previewsOfferApi = new PreviewTagApi(configuration);
            const offerPreviewV2RequestParameters: OfferPreviewGetV2Request = {
                ...this.getCommonRequestParams(commonHeaders),
                offerId,
                screenType,
            };
            const previewsOfferResponse = await previewsOfferApi.offerPreviewGetV2(
                offerPreviewV2RequestParameters,
            );

            const offerPreviewResponse: TOfferPreview = {
                offerPreview: previewsOfferResponse.data || {},
                networkCallId,
            };

            return offerPreviewResponse;
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(PreviewsServiceRepositoryImplV2.TAG, 'getOfferPreviewFromServiceV2()', error);
            throw error;
        }
    }

    private async getRecordingPreviewFromServiceV2(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        try {
            const commonHeaders = new CommonHeaders(recordingPreviewParams.applicationFeatureArea)
                .headers;
            const recordingPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(recordingPreviewConfigParameters);
            const previewsApi = new PreviewTagApi(configuration);
            const recordingPreviewV2RequestParameters: RecordingPreviewGetV2Request = {
                ...this.getCommonRequestParams(commonHeaders),
                recordingId: recordingPreviewParams.recordingId,
                channelNumber: recordingPreviewParams.channelNumber,
                groups: recordingPreviewParams.groups,
            };
            const previewsRecordingResponse = await previewsApi.recordingPreviewGetV2(
                recordingPreviewV2RequestParameters,
            );

            const recordingPreviewResponse: TOfferPreview = {
                offerPreview: previewsRecordingResponse.data || {},
                networkCallId: recordingPreviewParams.networkCallId,
            };

            return recordingPreviewResponse;
        } catch (error: any) {
            error.networkCallId = recordingPreviewParams.networkCallId;
            Log.e(PreviewsServiceRepositoryImplV2.TAG, 'getRecordingPreviewFromServiceV2()', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchContentPreview(contentId: string): Promise<TContentPreview> {
        try {
            return await this.getContentPreviewFromServiceV2(contentId);
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferPreview(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            return await this.getOfferPreviewFromServiceV2(offerId, screenType, networkCallId);
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingPreview(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        try {
            return await this.getRecordingPreviewFromServiceV2(recordingPreviewParams);
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public fetchUnifiedCollectionPreview(
        unifiedCollectionPreviewParams: TUnifiedCollectionPreviewParams,
    ): Promise<TUnifiedCollectionPreview> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchVodOfferPreview(
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        throw new Error('Method not implemented.');
    }
}
